import { component } from 'picoapp';
import gsap from 'gsap';
import {
  disableBodyScroll,
  enableBodyScroll
} from 'body-scroll-lock';

class QuickSearch extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.inner = this.querySelector('.QuickSearch__inner')
    this.backdrop = this.querySelector('.backdrop')
    this.input = this.querySelector('input')
    this.productTypes = [
      'American Flag',
      'Flagpole',
      'Custom Flag',
      'Porch Flag Kit',
      'Flag Accessories'
    ];
    this.currentTypeIndex = 0;
    this.typewriterTimeline = null;
    this.baseText = 'Find your ';
    this.cursorChar = '|';
    this.cursorVisible = true;
  }

  startTypewriterEffect() {
    if (this.typewriterTimeline) {
      this.typewriterTimeline.kill();
    }

    this.input.placeholder = '';
    
    // Create the main timeline
    this.typewriterTimeline = gsap.timeline();

    // Create cursor blink animation
    const cursorTimeline = gsap.timeline({
      repeat: -1,
    });

    // Simpler cursor blink that toggles between visible and hidden
    cursorTimeline
      .to({}, {
        duration: 0.3,
        onComplete: () => {
          const currentText = this.input.placeholder;
          this.cursorVisible = !this.cursorVisible;
          
          if (currentText) {
            this.input.placeholder = this.cursorVisible 
              ? currentText.endsWith(this.cursorChar) 
                ? currentText 
                : currentText + this.cursorChar
              : currentText.endsWith(this.cursorChar) 
                ? currentText.slice(0, -1) 
                : currentText;
          }
        }
      });

    // Initial animation for "Find your" - happens only once
    const baseLetters = this.baseText.split('');
    baseLetters.forEach((letter, index) => {
      this.typewriterTimeline.to(this.input, {
        placeholder: this.baseText.substring(0, index + 1) + this.cursorChar,
        duration: 0.1,
        ease: "none"
      });
    });

    // Create a nested timeline for the cycling product types
    const productTypesTimeline = gsap.timeline({
      repeat: -1,
    });

    // Function to create animation for each product type
    const createProductTypeAnimation = (productType) => {
      const letters = productType.split('');
      
      // Instantly set to base text before starting the new animation
      productTypesTimeline.set(this.input, {
        placeholder: this.baseText + this.cursorChar
      });

      // Type the product type letter by letter
      letters.forEach((letter, index) => {
        productTypesTimeline.to(this.input, {
          placeholder: this.baseText + productType.substring(0, index + 1) + this.cursorChar,
          duration: 0.1,
          ease: "none"
        });
      });

      // Pause at the end
      productTypesTimeline.to({}, { duration: 2 });
    };

    // Add animations for all product types
    this.productTypes.forEach((productType) => {
      createProductTypeAnimation(productType);
    });

    // Add the cursor animation first
    this.typewriterTimeline.add(cursorTimeline, 0);
    // Add the product cycling timeline after the base text animation
    this.typewriterTimeline.add(productTypesTimeline);
  }

  stopTypewriterEffect() {
    if (this.typewriterTimeline) {
      this.typewriterTimeline.kill();
      this.typewriterTimeline = null;
      this.cursorVisible = false;
      this.input.placeholder = 'Search...';
    }
  }

  connectedCallback() {
    // Start typewriter effect immediately when component is connected
    this.startTypewriterEffect();

    this.tl = gsap.timeline({
      paused: true,
      onComplete: () => {
        this.isOpen = true;
        disableBodyScroll(this.inner);
        this.querySelector('input').focus();
      },
      onReverseComplete: () => {
        this.isOpen = false;
        enableBodyScroll(this.inner);
        this.querySelector('input').blur();
      },
    })

    this.tl.to(this, { display: 'block', duration: 0.3, ease: 'power.out' })
      .to(this.backdrop, { opacity: 1, duration: 0.3, ease: 'power.out' })
      .to(this.inner, { y: 0, duration: 0.3, ease: 'power.out' })

    this.backdrop.addEventListener('click', () => {
      this.tl.reverse()
    })

    // Stop typewriter effect when user starts typing
    this.input.addEventListener('input', (e) => {
      if (e.inputType) { // Only if it's actual user input, not our typewriter
        this.stopTypewriterEffect();
      }
    });

    // Restart typewriter effect when input is cleared and unfocused
    this.input.addEventListener('blur', () => {
      if (this.input.value === '') {
        this.startTypewriterEffect();
      }
    });
  }

  toggle() {
    if (this.isOpen) {
      this.tl.reverse()
    } else {
      this.tl.play();
    }
  }
};

class QuickSearchTrigger extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('click', () => {
      document.querySelector('quick-search')?.toggle()
    })
  }
};

export default component((node, ctx) => {
  if (!customElements.get('quick-search')) {
    customElements.define('quick-search', QuickSearch);
  }
  if (!customElements.get('quick-search-trigger')) {
    customElements.define('quick-search-trigger', QuickSearchTrigger);
  }
});
